import { useState, useEffect, useCallback } from 'react';
import { isPast } from 'date-fns';
import { IndexedDBScheduleRepository } from '@/lib/repositories/IndexedDBScheduleRepository';
import { validateBakeTime, generateSchedule, formatMilestoneTime } from '@/lib/scheduleUtils';
import { formatRelativeTime } from '@/lib/dateUtils';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { TimelineCard } from '@/components/ui/TimelineCard';
import { ErrorState } from '@/components/ui/ErrorState';
import type { Schedule, Milestone } from '@/types/schedule';
import styles from '@/styles/components/SchedulePage.module.css';

const scheduleRepo = new IndexedDBScheduleRepository();

type MilestoneStatus = 'completed' | 'next' | 'upcoming' | 'goal';

function getMilestoneStatus(milestone: Milestone, isNext: boolean, isLast: boolean): MilestoneStatus {
  if (milestone.completed) return 'completed';
  if (isLast) return 'goal';
  if (isNext) return 'next';
  return 'upcoming';
}

export function SchedulePage() {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [bakeDateTime, setBakeDateTime] = useState('');
  const [validationError, setValidationError] = useState('');
  const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(null);

  const loadSchedules = useCallback(async () => {
    try {
      setLoadError(null);
      await scheduleRepo.archiveOldSchedules();
      const active = await scheduleRepo.getActiveSchedules();
      setSchedules(active);
      if (active.length > 0 && !selectedScheduleId) {
        setSelectedScheduleId(active[0].id);
      }
    } catch {
      setLoadError('Failed to load schedules. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [selectedScheduleId]);

  useEffect(() => {
    loadSchedules();
  }, [loadSchedules]);

  const handleCreateSchedule = useCallback(async () => {
    if (!bakeDateTime) {
      setValidationError('Please select a date and time.');
      return;
    }

    const validation = validateBakeTime(bakeDateTime);
    if (!validation.valid) {
      setValidationError(validation.error ?? 'Invalid bake time.');
      return;
    }

    try {
      const milestones = generateSchedule(bakeDateTime);
      const newSchedule = await scheduleRepo.createSchedule(bakeDateTime, milestones);
      setSchedules((prev) => [...prev, newSchedule]);
      setSelectedScheduleId(newSchedule.id);
      setShowForm(false);
      setBakeDateTime('');
      setValidationError('');
    } catch {
      setValidationError('Failed to create schedule.');
    }
  }, [bakeDateTime]);

  const handleDeleteSchedule = useCallback(
    async (id: string) => {
      try {
        await scheduleRepo.deleteSchedule(id);
        setSchedules((prev) => prev.filter((s) => s.id !== id));
        if (selectedScheduleId === id) {
          setSelectedScheduleId(null);
        }
      } catch {
        // Failed to delete
      }
    },
    [selectedScheduleId],
  );

  const handleCompleteMilestone = useCallback(
    async (milestoneId: string) => {
      if (!selectedScheduleId) return;
      try {
        const updated = await scheduleRepo.completeMilestone(selectedScheduleId, milestoneId);
        setSchedules((prev) =>
          prev.map((s) => (s.id === updated.id ? updated : s)),
        );
      } catch {
        // Failed to complete
      }
    },
    [selectedScheduleId],
  );

  if (isLoading) {
    return <div className={styles.page}>Loading schedules...</div>;
  }

  if (loadError) {
    return (
      <div className={styles.page}>
        <h1 className={styles.pageTitle}>Baking Schedule</h1>
        <ErrorState message={loadError} onRetry={loadSchedules} />
      </div>
    );
  }

  const selectedSchedule = schedules.find((s) => s.id === selectedScheduleId);

  // Get min datetime for the input (now + a small buffer)
  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  };

  return (
    <div className={styles.page}>
      <h1 className={styles.pageTitle}>Baking Schedule</h1>

      {/* Create Schedule Form */}
      {showForm ? (
        <Card>
          <div className={styles.formCard}>
            <label className={styles.fieldLabel} htmlFor="bakeDateTime">
              When do you want bread ready?
            </label>
            <input
              id="bakeDateTime"
              type="datetime-local"
              className={styles.dateInput}
              value={bakeDateTime}
              onChange={(e) => {
                setBakeDateTime(e.target.value);
                setValidationError('');
              }}
              min={getMinDateTime()}
            />
            {validationError && (
              <p className={styles.validationError} role="alert">
                {validationError}
              </p>
            )}
            <Button onClick={handleCreateSchedule}>Generate Schedule</Button>
            <Button variant="ghost" onClick={() => { setShowForm(false); setValidationError(''); }}>
              Cancel
            </Button>
          </div>
        </Card>
      ) : (
        <Button onClick={() => setShowForm(true)}>📅 Plan a Bake</Button>
      )}

      {/* Active Schedules List */}
      {schedules.length > 0 && (
        <Card>
          <h2 className={styles.timelineTitle}>Active Schedules</h2>
          <div className={styles.scheduleList} style={{ marginTop: 'var(--spacing-md)' }}>
            {schedules.map((schedule) => (
              <div
                key={schedule.id}
                className={styles.scheduleItem}
                onClick={() => setSelectedScheduleId(schedule.id)}
                style={{
                  cursor: 'pointer',
                  backgroundColor:
                    selectedScheduleId === schedule.id
                      ? 'rgba(212, 165, 116, 0.1)'
                      : 'transparent',
                  padding: 'var(--spacing-sm)',
                  borderRadius: 'var(--radius-lg)',
                }}
              >
                <div className={styles.scheduleInfo}>
                  <div className={styles.scheduleDate}>
                    🍞 {formatMilestoneTime(schedule.bakeDate)}
                  </div>
                  <div className={styles.scheduleTime}>
                    {isPast(new Date(schedule.bakeDate))
                      ? 'Completed'
                      : formatRelativeTime(schedule.bakeDate)}
                  </div>
                </div>
                <button
                  className={styles.deleteSchedule}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteSchedule(schedule.id);
                  }}
                  aria-label={`Delete schedule for ${formatMilestoneTime(schedule.bakeDate)}`}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Selected Schedule Timeline */}
      {selectedSchedule && (
        <div className={styles.timelineSection}>
          <div className={styles.scheduleHeader}>
            <h2 className={styles.timelineTitle}>Timeline</h2>
            <span className={styles.scheduleBakeDate}>
              Ready: {formatMilestoneTime(selectedSchedule.bakeDate)}
            </span>
          </div>

          {selectedSchedule.milestones
            .sort((a, b) => a.order - b.order)
            .map((milestone, index, arr) => {
              const firstUncompleted = arr.findIndex((m) => !m.completed);
              const isNext = index === firstUncompleted;
              const isLast = index === arr.length - 1;
              const status = getMilestoneStatus(milestone, isNext, isLast);

              return (
                <TimelineCard
                  key={milestone.id}
                  milestone={milestone}
                  status={status}
                  onMarkComplete={handleCompleteMilestone}
                />
              );
            })}
        </div>
      )}

      {/* Empty State */}
      {schedules.length === 0 && !showForm && (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📅</div>
          <h2 className={styles.emptyTitle}>No Bakes Planned</h2>
          <p className={styles.emptyText}>
            Tap "Plan a Bake" to create your baking schedule. We'll work backward
            from your desired bread-ready time.
          </p>
        </div>
      )}
    </div>
  );
}
